import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Grid, List, Trash2, ShoppingCart, Search, X } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ProductGrid from '../home/ProductGrid';
import { useAuth } from '../../context/AuthContext';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import { getFavorites, removeFromFavorites } from '../../services/api';
import { ConfirmationModal, AlertModal } from '../../components/common/Modal';

const Favorites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  
  // Modal states
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [showRemoveSelectedConfirmModal, setShowRemoveSelectedConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('info');
  const [itemToRemove, setItemToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchFavorites();
  }, [user, navigate]);

  // Update your fetchFavorites function
const fetchFavorites = async () => {
  try {
    setLoading(true);
    const response = await getFavorites(user.user_id);
    
    if (response.success) {
      // Transform the data to match ProductCard's expected structure
      const transformedFavorites = (response.data || []).map(fav => {
        // Create images array from image_url
        const images = fav.image_url ? [{ image_url: fav.image_url }] : [];
        
        return {
          ...fav,
          // Ensure listing_id exists
          listing_id: fav.listing_id || fav.id,
          // Add images array
          images: images,
          // Map other properties to expected names
          is_boosted: fav.is_boosted || false,
          is_negotiable: fav.is_negotiable || 0,
          seller_rating: fav.seller_rating || 0,
        };
      });
      
      // // console.log('Transformed Favorites:', transformedFavorites); // Debug
      setFavorites(transformedFavorites);
    }
  } catch (error) {
    // // console.error('Error fetching favorites:', error);
  } finally {
    setLoading(false);
  }
};

  const showSuccessModalMessage = (message) => {
    setModalMessage(message);
    setModalType('success');
    setShowSuccessModal(true);
  };

  const showErrorModal = (message) => {
    setModalMessage(message);
    setModalType('error');
    setShowSuccessModal(true);
  };

  const handleRemoveFavorite = async (listingId) => {
    setItemToRemove(listingId);
    setShowRemoveConfirmModal(true);
  };

  const confirmRemoveFavorite = async () => {
    if (!itemToRemove) return;
    
    setIsRemoving(true);
    try {
      const response = await removeFromFavorites(user.user_id, itemToRemove);
      if (response.success) {
        setFavorites(favorites.filter(item => item.listing_id !== itemToRemove));
        setSelectedItems(selectedItems.filter(id => id !== itemToRemove));
        
        const itemName = favorites.find(f => f.listing_id === itemToRemove)?.title || 'Item';
        showSuccessModalMessage(`"${itemName}" removed from favorites`);
      } else {
        showErrorModal('Failed to remove from favorites. Please try again.');
      }
    } catch (error) {
      // console.error('Error removing from favorites:', error);
      showErrorModal('An error occurred. Please try again.');
    } finally {
      setIsRemoving(false);
      setShowRemoveConfirmModal(false);
      setItemToRemove(null);
    }
  };

  const handleRemoveSelected = async () => {
    if (selectedItems.length === 0) return;
    setShowRemoveSelectedConfirmModal(true);
  };

  const confirmRemoveSelected = async () => {
    if (selectedItems.length === 0) return;
    
    setIsRemoving(true);
    try {
      const removalPromises = selectedItems.map(listingId => 
        removeFromFavorites(user.user_id, listingId)
      );
      
      const results = await Promise.all(removalPromises);
      const allSuccess = results.every(r => r.success);
      
      if (allSuccess) {
        setFavorites(favorites.filter(item => !selectedItems.includes(item.listing_id)));
        
        if (selectedItems.length === 1) {
          const itemName = favorites.find(f => f.listing_id === selectedItems[0])?.title || 'Item';
          showSuccessModalMessage(`"${itemName}" removed from favorites`);
        } else {
          showSuccessModalMessage(`${selectedItems.length} items removed from favorites`);
        }
        
        setSelectedItems([]);
        setSelectAll(false);
      } else {
        showErrorModal('Some items could not be removed. Please try again.');
      }
    } catch (error) {
      // console.error('Error removing selected favorites:', error);
      showErrorModal('An error occurred. Please try again.');
    } finally {
      setIsRemoving(false);
      setShowRemoveSelectedConfirmModal(false);
    }
  };

  const handleAddToCart = (listing) => {
    // console.log('Added to cart:', listing);
    showSuccessModalMessage(`"${listing.title}" added to cart!`);
    // Implement actual add to cart logic here
  };

  const handleSelectItem = (listingId) => {
    setSelectedItems(prev => 
      prev.includes(listingId)
        ? prev.filter(id => id !== listingId)
        : [...prev, listingId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      const allIds = filteredFavorites.map(item => item.listing_id);
      setSelectedItems(allIds);
    }
    setSelectAll(!selectAll);
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectAll(false);
  };

  const filteredFavorites = favorites.filter(item => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        (item.category_name && item.category_name.toLowerCase().includes(query))
      );
    }
    return true;
  });

  // Enhanced favorites with selection state
  const enhancedFavorites = filteredFavorites.map(item => ({
    ...item,
    isSelected: selectedItems.includes(item.listing_id),
    onFavoriteClick: () => handleRemoveFavorite(item.listing_id),
    onSelect: () => handleSelectItem(item.listing_id),
    showSelection: true,
    isFavorite: true, // All items are favorites on this page
    showAddToCart: true,
    onAddToCart: () => handleAddToCart(item)
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-80 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Confirmation Modal for Single Removal */}
      <ConfirmationModal
        isOpen={showRemoveConfirmModal}
        onClose={() => !isRemoving && setShowRemoveConfirmModal(false)}
        title="Remove from Favorites"
        message="Are you sure you want to remove this item from your favorites?"
        confirmText={isRemoving ? "Removing..." : "Yes, Remove"}
        cancelText="Cancel"
        onConfirm={confirmRemoveFavorite}
        onCancel={() => setShowRemoveConfirmModal(false)}
        isLoading={isRemoving}
        type="warning"
        showCancel={!isRemoving}
        showConfirm={!isRemoving}
      />
      
      {/* Confirmation Modal for Bulk Removal */}
      <ConfirmationModal
        isOpen={showRemoveSelectedConfirmModal}
        onClose={() => !isRemoving && setShowRemoveSelectedConfirmModal(false)}
        title={`Remove ${selectedItems.length} Item${selectedItems.length > 1 ? 's' : ''}`}
        message={`Are you sure you want to remove ${selectedItems.length} selected item${selectedItems.length > 1 ? 's' : ''} from your favorites?`}
        confirmText={isRemoving ? "Removing..." : "Yes, Remove All"}
        cancelText="Cancel"
        onConfirm={confirmRemoveSelected}
        onCancel={() => setShowRemoveSelectedConfirmModal(false)}
        isLoading={isRemoving}
        type="warning"
        showCancel={!isRemoving}
        showConfirm={!isRemoving}
      />
      
      {/* Success/Error Modal */}
      <AlertModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={modalType === 'success' ? 'Success!' : 'Error'}
        message={modalMessage}
        type={modalType}
        buttonText="OK"
        onButtonClick={() => setShowSuccessModal(false)}
      />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 bg-white rounded-xl shadow-sm p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-pink-500 to-red-500 p-3 rounded-xl mr-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Favorites</h1>
                <p className="text-gray-600 mt-2">
                  {favorites.length} {favorites.length === 1 ? 'item' : 'items'} saved
                  {selectedItems.length > 0 && (
                    <span className="ml-2 text-pink-600 font-medium">
                      ({selectedItems.length} selected)
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-4 md:mt-0">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all shadow hover:shadow-lg"
              >
                Continue Shopping
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search in favorites..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isRemoving}
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Selection Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              {selectedItems.length > 0 ? (
                <>
                  <span className="text-sm font-medium text-gray-700">
                    {selectedItems.length} selected
                  </span>
                  <button
                    onClick={handleRemoveSelected}
                    disabled={isRemoving}
                    className="flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Selected
                  </button>
                  <button
                    onClick={clearSelection}
                    disabled={isRemoving}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={filteredFavorites.length === 0 || isRemoving}
                    className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-600">Select all</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                Found {filteredFavorites.length} items
              </span>
            </div>
          </div>
        </div>

        {/* Favorites Content */}
        {filteredFavorites.length > 0 ? (
          <>
            {/* Use ProductGrid component */}
            <ProductGrid 
              listings={enhancedFavorites}
              viewMode={viewMode}
              sortBy={sortBy}
              onViewModeChange={setViewMode}
              onSortChange={setSortBy}
              showControls={true}
              isSearchPage={false}
              hidePagination={filteredFavorites.length <= 12}
            />

            {/* Bulk Actions Footer */}
            {selectedItems.length > 0 && (
              <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">{selectedItems.length}</span> items selected
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        selectedItems.forEach(listingId => {
                          const item = favorites.find(f => f.listing_id === listingId);
                          if (item) handleAddToCart(item);
                        });
                      }}
                      disabled={isRemoving}
                      className="flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Add All to Cart
                    </button>
                    
                    <button
                      onClick={handleRemoveSelected}
                      disabled={isRemoving}
                      className="flex items-center px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:from-red-600 hover:to-pink-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Selected
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-pink-100 to-red-100 rounded-full flex items-center justify-center">
              <Heart className="w-12 h-12 text-pink-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No matching favorites found' : 'Your favorites list is empty'}
            </h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              {searchQuery
                ? 'Try a different search term.'
                : 'Save items you love by clicking the heart icon on any product.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {searchQuery ? (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                >
                  Clear Search
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                  >
                    Explore Products
                  </button>
                  <button
                    onClick={() => navigate('/search')}
                    className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Start Searching
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tips Section */}
        {filteredFavorites.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-6 border border-orange-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Favorites Tips</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg border border-orange-100">
                <div className="flex items-center mb-2">
                  <div className="bg-orange-100 p-2 rounded-lg mr-3">
                    <Heart className="w-5 h-5 text-orange-500" />
                  </div>
                  <h4 className="font-medium text-gray-900">Buy Smart</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Never pay directly to any seller account, instead use our Escrow.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-orange-100">
                <div className="flex items-center mb-2">
                  <div className="bg-orange-100 p-2 rounded-lg mr-3">
                    <ShoppingCart className="w-5 h-5 text-orange-500" />
                  </div>
                  <h4 className="font-medium text-gray-900">Quick Purchase</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Engage in a conversation with the Seller.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-orange-100">
                <div className="flex items-center mb-2">
                  <div className="bg-orange-100 p-2 rounded-lg mr-3">
                    <Trash2 className="w-5 h-5 text-orange-500" />
                  </div>
                  <h4 className="font-medium text-gray-900">Easy Cleanup</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Select and remove multiple items from your favorites easily.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile Bottom Navigation */}
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default Favorites;